import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../Auth/firebase";
import { ShieldCheck, Award, ArrowRight, Loader2, Sparkles } from "lucide-react";
import CommonLoader from "../CommonLoader";

const SchemesPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(null);
  const [schemeSettings, setSchemeSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    const fetchSchemeData = async () => {
      try {
        const docRef = doc(db, "schemes", "schemes");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSchemeSettings(docSnap.data() || {});
        }
      } catch (err) {
        console.error("Error fetching scheme settings:", err);
      }
    };

    fetchSchemeData();
    return () => unsubscribe();
  }, []);

  const handleSchemeSelect = async (schemeKey) => {
    if (!user) {
      navigate("/login");
      return;
    }

    setChecking(schemeKey);
    try {
      const paymentDocRef = doc(db, `${schemeKey}_payments`, user.uid);
      const requestDocRef = doc(db, `${schemeKey}_request`, user.uid);

      const [paymentSnap, requestSnap] = await Promise.all([
        getDoc(paymentDocRef),
        getDoc(requestDocRef),
      ]);

      if (paymentSnap.exists() || requestSnap.exists()) {
        navigate(`/passbook?scheme=${schemeKey}`);
      } else {
        navigate(`/register?scheme=${schemeKey}`);
      }
    } catch (error) {
      console.error("Error checking scheme registration status:", error);
      navigate(`/register?scheme=${schemeKey}`);
    } finally {
      setChecking(null);
    }
  };

  if (loading) {
    return <CommonLoader />;
  }

  const scheme1Details = schemeSettings?.scheme1_details || [
    "Flexible monthly payments",
    "No making charges up to 18%",
    "Pure 916 BIS Hallmarked Gold",
    "Start from just ₹1,000",
  ];

  const scheme2Details = schemeSettings?.scheme2_details || [
    "Gold rate lock facility",
    "Pay monthly installments",
    "Redeem as exquisite ornaments",
    "Wastage and making charges discount",
  ];

  return (
    <div className="min-h-screen bg-[#fafaf7] text-[#0b3a24] font-sans relative overflow-hidden">
      
      {/* Hero Header Banner (Dark Kerala Emerald Green & Gold) */}
      <section className="relative bg-gradient-to-br from-[#041a0f] via-[#09100d] to-[#020906] text-white pt-36 pb-24 px-4 sm:px-6 lg:px-8 border-b border-[#eddca6]/20 overflow-hidden">
        
        {/* Soft Ambient Light Glow Blobs */}
        <div className="absolute top-1/3 left-1/4 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-tr from-gold-900/20 to-transparent rounded-full filter blur-[140px] pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-[300px] h-[300px] bg-[#eddca6]/10 rounded-full filter blur-[100px] pointer-events-none"></div>

        <div className="max-w-5xl mx-auto text-center relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 bg-[#eddca6]/10 border border-[#eddca6]/30 text-[#eddca6] px-4.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#eddca6] animate-pulse" />
            EXCLUSIVE GOLD SAVINGS
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-black tracking-widest uppercase leading-tight text-white drop-shadow-md">
            CHOOSE YOUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#eddca6] via-[#f5edd2] to-[#c5a85a]">INVESTMENT SCHEME</span>
          </h1>

          <p className="max-w-xl mx-auto text-xs sm:text-sm text-white/80 font-light leading-relaxed">
            Invest monthly in our gold saving plans to lock gold rates and redeem premium BIS 916 ornaments with zero making charges.
          </p>

          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#eddca6] to-transparent mx-auto pt-2"></div>
        </div>
      </section>

      {/* Main Schemes Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#fafaf7] relative z-10">
        <div className="max-w-5xl mx-auto">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Scheme 1 Card */}
            <div 
              onClick={() => !checking && handleSchemeSelect("scheme1")}
              className={`group relative bg-gradient-to-br from-white via-[#fafaf7] to-white border border-gold-300 rounded-3xl p-8 hover:border-[#0b3a24]/40 hover:scale-[1.02] hover:shadow-2xl hover:shadow-gold-500/10 transition-all duration-500 cursor-pointer flex flex-col justify-between overflow-hidden min-h-[420px] shadow-sm ${
                checking === "scheme1" ? "pointer-events-none opacity-80" : ""
              }`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-300 rounded-full blur-3xl opacity-[0.03] group-hover:opacity-[0.07] transition-opacity duration-500 -mr-16 -mt-16"></div>
              
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 bg-white border border-gold-300 rounded-2xl flex items-center justify-center group-hover:bg-[#0b3a24] group-hover:border-[#0b3a24] transition-colors duration-300">
                    <Award className="w-6 h-6 text-[#0b3a24] group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-[9px] font-bold text-[#0b3a24] bg-[#0b3a24]/10 border border-[#0b3a24]/20 px-3 py-1 rounded-md tracking-widest uppercase font-sans">Plan A</span>
                </div>
                
                <h3 className="text-2xl font-serif font-black text-[#0b3a24] mb-3">Scheme 1</h3>
                <p className="text-[#0b3a24]/80 text-xs sm:text-sm mb-6 leading-relaxed font-light">
                  Protect your savings from market volatility by locking your gold rate now and securing maximum value.
                </p>
                
                <div className="space-y-3.5 border-t border-gold-200 pt-6">
                  {scheme1Details.map((detail, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <ShieldCheck className="w-4 h-4 text-[#0d5c3a] flex-shrink-0 mt-0.5" />
                      <span className="text-[#0b3a24] text-xs sm:text-sm font-medium">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gold-200 flex items-center justify-between">
                <span className="text-xs font-semibold tracking-widest uppercase text-[#0b3a24] group-hover:text-[#c5a85a] transition-colors font-sans">
                  {checking === "scheme1" ? "Checking details..." : "Proceed to Scheme"}
                </span>
                <button 
                  className="w-10 h-10 rounded-xl bg-white border border-gold-300 group-hover:bg-[#0b3a24] group-hover:border-[#0b3a24] flex items-center justify-center transition-all duration-300"
                  disabled={checking === "scheme1"}
                >
                  {checking === "scheme1" ? (
                    <Loader2 className="w-4 h-4 text-[#0b3a24] animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4 text-[#0b3a24] group-hover:text-white transition-colors duration-300" />
                  )}
                </button>
              </div>
            </div>

            {/* Scheme 2 Card */}
            <div 
              onClick={() => !checking && handleSchemeSelect("scheme2")}
              className={`group relative bg-gradient-to-br from-white via-[#fafaf7] to-white border border-gold-300 rounded-3xl p-8 hover:border-[#0b3a24]/40 hover:scale-[1.02] hover:shadow-2xl hover:shadow-gold-500/15 transition-all duration-500 cursor-pointer flex flex-col justify-between overflow-hidden min-h-[420px] shadow-sm ${
                checking === "scheme2" ? "pointer-events-none opacity-80" : ""
              }`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-300 rounded-full blur-3xl opacity-[0.03] group-hover:opacity-[0.07] transition-opacity duration-500 -mr-16 -mt-16"></div>
              
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 bg-white border border-gold-300 rounded-2xl flex items-center justify-center group-hover:bg-[#0b3a24] group-hover:border-[#0b3a24] transition-colors duration-300">
                    <Award className="w-6 h-6 text-[#0b3a24] group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-[9px] font-bold text-[#0b3a24] bg-[#0b3a24]/10 border border-[#0b3a24]/20 px-3 py-1 rounded-md tracking-widest uppercase font-sans">Plan B</span>
                </div>
                
                <h3 className="text-2xl font-serif font-black text-[#0b3a24] mb-3">Scheme 2</h3>
                <p className="text-[#0b3a24]/80 text-xs sm:text-sm mb-6 leading-relaxed font-light">
                  A highly secure and convenient savings plan designed for dynamic investors seeking flexibility and premium returns.
                </p>
                
                <div className="space-y-3.5 border-t border-gold-200 pt-6">
                  {scheme2Details.map((detail, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <ShieldCheck className="w-4 h-4 text-[#0d5c3a] flex-shrink-0 mt-0.5" />
                      <span className="text-[#0b3a24] text-xs sm:text-sm font-medium">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gold-200 flex items-center justify-between">
                <span className="text-xs font-semibold tracking-widest uppercase text-[#0b3a24] group-hover:text-[#c5a85a] transition-colors font-sans">
                  {checking === "scheme2" ? "Checking details..." : "Proceed to Scheme"}
                </span>
                <button 
                  className="w-10 h-10 rounded-xl bg-white border border-gold-300 group-hover:bg-[#0b3a24] group-hover:border-[#0b3a24] flex items-center justify-center transition-all duration-300"
                  disabled={checking === "scheme2"}
                >
                  {checking === "scheme2" ? (
                    <Loader2 className="w-4 h-4 text-[#0b3a24] animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4 text-[#0b3a24] group-hover:text-white transition-colors duration-300" />
                  )}
                </button>
              </div>
            </div>

          </div>

        </div>
      </section>

    </div>
  );
};

export default SchemesPage;
